import { Navigate, useParams } from "react-router-dom";
import FaqAccordion from "../../components/sections/FaqAccordion";
import PageHero from "../../components/sections/PageHero";
import GoldButton from "../../components/ui/GoldButton";
import SectionLabel from "../../components/ui/SectionLabel";

type FAQItem = {
  question: string;
  answer: string;
};

type ProductContent = {
  slug: string;
  label: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  tags: string[];
  howItWorks: string[];
  highlights: [
    { heading: string; body: string },
    { heading: string; body: string }
  ];
  cta: string;
  faqs: FAQItem[];
};

const productContent: Record<string, ProductContent> = {
  casemind: {
    slug: "casemind",
    label: "PRODUCTS / OWL CASE MIND",
    title: "Owl Case Mind - Litigation Management",
    titleAccent: "for Indian Law Firms.",
    subtitle: "Organize, track, and act on your entire caseload in one unified platform without the overhead of legacy software.",
    tags: ["Go Microservices", "Next.js", "PostgreSQL (RLS)", "Kubernetes", "Multi-tenant", "eCourts Sync"],
    howItWorks: [
      "Centralized Dashboard: Access case files, hearings, clients, advocates, and documents all in one place.",
      "eCourts Auto-Sync: Pull live hearing dates and case updates automatically with zero manual entry.",
      "Automated Reminders & Triage: Sends hearing reminders and flags compliance items to surface urgent cases instantly."
    ],
    highlights: [
      {
        heading: "DESIGNED FOR INDIAN LAW FIRMS",
        body: "Reliable, organized case tracking tailored for the Indian legal ecosystem. Fully isolated data architecture ensures security and peace of mind.",
      },
      {
        heading: "DOCUMENT & ORDER MANAGEMENT",
        body: "Parses and stores court order PDFs, deduplicating them and automatically linking them to the correct matter.",
      },
    ],
    cta: "REQUEST DEMO",
    faqs: [
      { question: "How does the eCourts sync work?", answer: "Owl Case Mind automatically pulls live hearing dates and case updates directly from the eCourts portal, eliminating the need for manual data entry." },
      { question: "Is my firm's data secure?", answer: "Yes. The platform is built multi-tenant from the ground up, with PostgreSQL row-level security ensuring each firm's data is fully isolated and secure." },
      { question: "Does it manage court orders?", answer: "Absolutely. It parses and stores court order PDFs, deduplicating them and automatically linking them to the right matter." },
    ],
  },
  chatdesk: {
    slug: "chatdesk",
    label: "PRODUCTS / CHATDESK",
    title: "ChatDesk - AI support chatbot",
    titleAccent: "for your knowledge base.",
    subtitle: "Deploy a support assistant trained on your docs, SOPs, and product FAQs.",
    tags: ["RAG", "WhatsApp + Web Chat", "Helpdesk Integrations", "Role-based Access", "Analytics", "Beta"],
    howItWorks: [
      "Connect help articles, internal SOPs, PDFs, and ticket history.",
      "ChatDesk answers customer questions with grounded responses and source traceability.",
      "Escalates unresolved queries to human agents with full context handover.",
    ],
    highlights: [
      {
        heading: "SUPPORT TEAM READY",
        body: "Designed for Indian SMB and mid-market support teams with fast setup and low-maintenance knowledge updates.",
      },
      {
        heading: "BETA ACCESS",
        body: "Currently in beta with onboarding support, analytics dashboard, and custom fallback playbooks.",
      },
    ],
    cta: "REQUEST BETA ACCESS",
    faqs: [
      { question: "Can it use our existing docs?", answer: "Yes. It can ingest documentation, policies, and historical support content." },
      { question: "Can we hand over to human agents?", answer: "Yes. ChatDesk supports escalation with conversation context and user metadata." },
      { question: "Is multilingual support available?", answer: "English and Hindi are supported in beta, with additional language packs planned." },
    ],
  },
  callagent: {
    slug: "callagent",
    label: "PRODUCTS / CALLAGENT",
    title: "CallAgent - AI voice operations",
    titleAccent: "for Indian SMBs.",
    subtitle: "Automate inbound and outbound calling with AI voice workflows and CRM sync.",
    tags: ["Voice AI", "Outbound Sequences", "Call Routing", "CRM Sync", "Compliance Logs", "Coming Soon"],
    howItWorks: [
      "Configure call flows for lead qualification, reminders, support, and collections.",
      "AI voice handles calls with intent detection and response templates tuned to your business.",
      "Call outcomes, transcripts, and sentiment signals sync to your CRM dashboard.",
    ],
    highlights: [
      {
        heading: "VOICE AUTOMATION",
        body: "Purpose-built for high-volume Indian SMB workflows including lead follow-up, reminders, and routine customer service.",
      },
      {
        heading: "LAUNCH STATUS",
        body: "Product is in pre-release. Early design partners can join to shape call scripts and operational workflows.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Does it support Indian telecom providers?", answer: "Yes, native integration planning includes major India-focused telephony providers." },
      { question: "Can we review call transcripts?", answer: "Yes, transcripts and call logs are available for QA, compliance, and coaching." },
      { question: "Can humans take over live calls?", answer: "Human handoff workflows are part of the core product roadmap." },
    ],
  },
  crm: {
    slug: "crm",
    label: "PRODUCTS / CRM",
    title: "OWL CRM - configurable CRM",
    titleAccent: "for Indian businesses.",
    subtitle: "A customisable sales and operations CRM without unnecessary enterprise overhead.",
    tags: ["Lead Pipeline", "Custom Workflows", "Role Permissions", "Reports", "Integrations", "Coming Soon"],
    howItWorks: [
      "Model your lead, account, and deal stages around your existing sales process.",
      "Automate repetitive steps like lead assignment, reminders, and task updates.",
      "Track team performance with configurable dashboards and activity logs.",
    ],
    highlights: [
      {
        heading: "FLEXIBLE CRM CORE",
        body: "Designed for Indian teams that need practical customization, faster adoption, and reliable process enforcement.",
      },
      {
        heading: "ROADMAP",
        body: "Launching with lead, pipeline, and team operations modules first, followed by deeper automation packs.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Can this replace spreadsheets?", answer: "Yes. It is designed to centralize leads, tasks, and follow-ups in one workflow." },
      { question: "Can we customize stages and fields?", answer: "Yes. Workflow and field-level customization is a core capability." },
      { question: "Will migration support be available?", answer: "Yes. Guided migration support is planned for launch customers." },
    ],
  },
  contentengine: {
    slug: "contentengine",
    label: "PRODUCTS / CONTENTENGINE",
    title: "ContentEngine - AI marketing content",
    titleAccent: "for growth teams.",
    subtitle: "Generate campaign-ready marketing content with brand-safe prompts and workflow controls.",
    tags: ["Campaign Briefs", "Brand Voice", "SEO Drafting", "Ad Copy", "Approval Flows", "Coming Soon"],
    howItWorks: [
      "Define brand voice, offer context, audience segments, and campaign goals.",
      "Generate drafts for blogs, ads, emails, and social content in one workflow.",
      "Route outputs through approval checkpoints before publishing.",
    ],
    highlights: [
      {
        heading: "MARKETING FOCUS",
        body: "Built for teams that need high output velocity while preserving consistency across channels and campaigns.",
      },
      {
        heading: "STATUS",
        body: "In development with launch focus on SEO content and paid campaign creative operations.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Can we lock brand voice guidelines?", answer: "Yes. Brand and compliance rules can be configured as reusable constraints." },
      { question: "Can agencies use this for multiple clients?", answer: "Yes. Multi-workspace and white-label options are part of the roadmap." },
      { question: "Will it support SEO briefs?", answer: "Yes. SEO-first content workflows are included in the initial release plan." },
    ],
  },
  leadflow: {
    slug: "leadflow",
    label: "PRODUCTS / LEADFLOW",
    title: "LeadFlow - AI lead generation",
    titleAccent: "and scoring platform.",
    subtitle: "Improve lead quality and follow-up speed with AI-assisted targeting and scoring.",
    tags: ["Lead Discovery", "Scoring Models", "Lead Routing", "Outbound Sync", "Dashboards", "Coming Soon"],
    howItWorks: [
      "Ingest lead sources from forms, campaigns, and outbound channels.",
      "Score leads based on fit, intent signals, and historical conversion patterns.",
      "Auto-route high-priority leads to the right rep and sequence.",
    ],
    highlights: [
      {
        heading: "SALES EFFICIENCY",
        body: "Built to reduce response-time gaps and focus teams on leads with highest conversion potential.",
      },
      {
        heading: "STATUS",
        body: "Coming soon with design support for B2B and service-led Indian sales teams.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Can it work with our CRM?", answer: "Yes. LeadFlow is designed to sync with CRM workflows and ownership rules." },
      { question: "How are leads scored?", answer: "Scores combine profile fit, behavior signals, and configurable weighting." },
      { question: "Does it support inbound and outbound?", answer: "Yes. The roadmap covers both inbound capture and outbound prioritization." },
    ],
  },
  insightboard: {
    slug: "insightboard",
    label: "PRODUCTS / INSIGHTBOARD",
    title: "InsightBoard - AI analytics",
    titleAccent: "for business decisions.",
    subtitle: "Track performance, surface anomalies, and get AI-driven operational insights.",
    tags: ["KPI Dashboards", "Anomaly Detection", "Forecasting", "Data Connectors", "Alerts", "Coming Soon"],
    howItWorks: [
      "Connect CRM, sales, support, and marketing data sources.",
      "Build live dashboards with AI-generated trend summaries and alerts.",
      "Monitor leading indicators and forecast changes before they impact revenue.",
    ],
    highlights: [
      {
        heading: "DECISION INTELLIGENCE",
        body: "Designed for leadership and operations teams that need signal clarity without BI complexity overload.",
      },
      {
        heading: "STATUS",
        body: "Product is in roadmap execution with controlled previews opening for pilot teams.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Do we need a data team to use it?", answer: "No. InsightBoard is designed for business users with guided setup." },
      { question: "Can it trigger alerts automatically?", answer: "Yes. Alerting and threshold rules are part of the core product plan." },
      { question: "Will custom metrics be supported?", answer: "Yes. Teams can define custom KPIs and reporting views." },
    ],
  },
  contractreader: {
    slug: "contractreader",
    label: "PRODUCTS / CONTRACTREADER",
    title: "ContractReader - AI contract review",
    titleAccent: "and risk detection.",
    subtitle: "Spot legal red flags, risky clauses, and missing protections in minutes.",
    tags: ["Clause Detection", "Risk Flags", "Summary Reports", "Version Compare", "Legal Workflows", "Coming Soon"],
    howItWorks: [
      "Upload contracts and policy documents for clause-level analysis.",
      "AI identifies risky language, obligations, and missing safeguards.",
      "Generate plain-language summaries and review notes for legal or business teams.",
    ],
    highlights: [
      {
        heading: "LEGAL REVIEW ACCELERATION",
        body: "Built for in-house legal and contracting teams handling high document volume with tight turnaround.",
      },
      {
        heading: "STATUS",
        body: "In development with initial focus on standard commercial contracts and legal review pipelines.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Is this a replacement for legal counsel?", answer: "No. It assists legal teams by accelerating first-pass review and issue spotting." },
      { question: "Can it detect non-standard clauses?", answer: "Yes. It flags deviations from expected clause patterns and risk rules." },
      { question: "Will it support Indian legal use cases?", answer: "Yes. Indian business and legal context is part of the product design focus." },
    ],
  },
  reviewpulse: {
    slug: "reviewpulse",
    label: "PRODUCTS / REVIEWPULSE",
    title: "ReviewPulse - AI reputation",
    titleAccent: "monitoring and response.",
    subtitle: "Track and respond to online reviews with consistent tone and faster turnarounds.",
    tags: ["Review Aggregation", "Response Drafting", "Sentiment Tracking", "Escalation Rules", "Multi-location", "Coming Soon"],
    howItWorks: [
      "Collect reviews from platforms into a unified reputation dashboard.",
      "Generate response drafts based on tone rules, escalation policy, and context.",
      "Track sentiment trends and unresolved issues by location or team.",
    ],
    highlights: [
      {
        heading: "REPUTATION OPERATIONS",
        body: "Built for brands, clinics, and service businesses that need structured review handling across channels.",
      },
      {
        heading: "STATUS",
        body: "Planned release with focus on response speed, governance controls, and actionable sentiment reporting.",
      },
    ],
    cta: "JOIN WAITLIST",
    faqs: [
      { question: "Can we approve AI responses before posting?", answer: "Yes. Human approval workflows are central to the response pipeline." },
      { question: "Can it manage multiple branches?", answer: "Yes. Multi-location views and ownership routing are planned." },
      { question: "Will it show sentiment trends over time?", answer: "Yes. Trend analysis and issue clustering are included in the roadmap." },
    ],
  },
};

const ProductDetail = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const product = productSlug ? productContent[productSlug] : undefined;

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  return (
    <>
      <PageHero
        label={product.label}
        title={product.title}
        titleAccent={product.titleAccent}
        subtitle={product.subtitle}
      />

      <section style={{ background: "#050505", padding: "0 0 120px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px)" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 36 }}>
            {product.tags.map((item) => (
              <span
                key={item}
                style={{
                  padding: "9px 14px",
                  border: "1px solid rgba(212,168,67,0.24)",
                  borderRadius: 3,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  color: "#D4A843",
                  background: "rgba(212,168,67,0.06)",
                }}
              >
                {item}
              </span>
            ))}
          </div>

          <div style={{ background: "#0E0E0E", border: "1px solid rgba(212,168,67,0.18)", borderRadius: 4, padding: "30px clamp(24px, 4vw, 36px)", marginBottom: 36 }}>
            <SectionLabel text="HOW IT WORKS" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 22, marginTop: 22 }}>
              {product.howItWorks.map((item, index) => (
                <div key={item} style={{ background: "#111010", border: "1px solid rgba(212,168,67,0.14)", borderRadius: 4, padding: 22 }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.18em", color: "#D4A843", marginBottom: 10 }}>
                    STEP {index + 1}
                  </div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F8F4EC", lineHeight: 1.75 }}>{item}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginBottom: 36 }}>
            {product.highlights.map((card) => (
              <div key={card.heading} style={{ background: "#0E0E0E", border: "1px solid rgba(212,168,67,0.18)", borderRadius: 4, padding: 28 }}>
                <SectionLabel text={card.heading} />
                <p style={{ marginTop: 16, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F8F4EC", lineHeight: 1.8 }}>
                  {card.body}
                </p>
              </div>
            ))}

            </div>

          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <GoldButton href="/contact">{product.cta}</GoldButton>
          </div>

          <SectionLabel text="FAQ" />
          <div style={{ marginTop: 22 }}>
            <FaqAccordion items={product.faqs} />
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductDetail;
